/**
 * Servicios para gestión de asistencia
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  Timestamp,
  writeBatch,
  onSnapshot,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { convertTimestamps } from "./base";
import type {
  Meeting,
  Attendance,
  AttendanceStatus,
  MeetingType,
} from "@/types";

const MEETINGS_COLLECTION = "meetings";
const ATTENDANCE_COLLECTION = "attendance";

// ==================== MEETING SERVICE ====================

export const meetingService = {
  async getById(id: string): Promise<Meeting | null> {
    const docRef = doc(db, MEETINGS_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as Meeting;
  },

  async getByClub(clubId: string): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      where("clubId", "==", clubId),
      orderBy("date", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Meeting,
    );
  },

  async getByClubAndPeriod(
    clubId: string,
    periodId: string,
  ): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      where("clubId", "==", clubId),
      where("periodId", "==", periodId),
      orderBy("date", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Meeting,
    );
  },

  async getByType(clubId: string, type: MeetingType): Promise<Meeting[]> {
    const q = query(
      collection(db, MEETINGS_COLLECTION),
      where("clubId", "==", clubId),
      where("type", "==", type),
      orderBy("date", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Meeting,
    );
  },

  async create(
    data: Omit<
      Meeting,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
    userId: string,
  ): Promise<string> {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, MEETINGS_COLLECTION), {
      ...data,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
    });
    return docRef.id;
  },

  async update(
    id: string,
    data: Partial<Meeting>,
    userId?: string,
  ): Promise<void> {
    const docRef = doc(db, MEETINGS_COLLECTION, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now(),
      ...(userId && { updatedBy: userId }),
    });
  },

  async closeMeeting(
    id: string,
    attendanceCount: number,
    totalMembers?: number,
  ): Promise<void> {
    const docRef = doc(db, MEETINGS_COLLECTION, id);
    await updateDoc(docRef, {
      isClosed: true,
      attendanceCount,
      ...(totalMembers != null && { totalMembers }),
      updatedAt: Timestamp.now(),
    });
  },

  async reopenMeeting(id: string): Promise<void> {
    const docRef = doc(db, MEETINGS_COLLECTION, id);
    await updateDoc(docRef, {
      isClosed: false,
      updatedAt: Timestamp.now(),
    });
  },

  async delete(id: string): Promise<void> {
    // También eliminar todos los registros de asistencia asociados
    const attendanceQuery = query(
      collection(db, ATTENDANCE_COLLECTION),
      where("meetingId", "==", id),
    );
    const attendanceSnapshot = await getDocs(attendanceQuery);

    const batch = writeBatch(db);
    attendanceSnapshot.docs.forEach((doc) => {
      batch.delete(doc.ref);
    });
    batch.delete(doc(db, MEETINGS_COLLECTION, id));
    await batch.commit();
  },
};

// ==================== ATTENDANCE SERVICE ====================

export const attendanceService = {
  async getById(id: string): Promise<Attendance | null> {
    const docRef = doc(db, ATTENDANCE_COLLECTION, id);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) return null;
    return { id: docSnap.id, ...docSnap.data() } as Attendance;
  },

  async getByMeeting(meetingId: string): Promise<Attendance[]> {
    const q = query(
      collection(db, ATTENDANCE_COLLECTION),
      where("meetingId", "==", meetingId),
      orderBy("personName", "asc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Attendance,
    );
  },

  async getByEvent(eventId: string): Promise<Attendance[]> {
    const q = query(
      collection(db, ATTENDANCE_COLLECTION),
      where("eventId", "==", eventId),
      orderBy("personName", "asc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Attendance,
    );
  },

  async getByPerson(personId: string): Promise<Attendance[]> {
    const q = query(
      collection(db, ATTENDANCE_COLLECTION),
      where("personId", "==", personId),
      orderBy("date", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Attendance,
    );
  },

  async getByMember(memberId: string): Promise<Attendance[]> {
    const q = query(
      collection(db, ATTENDANCE_COLLECTION),
      where("memberId", "==", memberId),
      orderBy("date", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Attendance,
    );
  },

  async getByClubAndDateRange(
    clubId: string,
    startDate: Timestamp,
    endDate: Timestamp,
  ): Promise<Attendance[]> {
    const q = query(
      collection(db, ATTENDANCE_COLLECTION),
      where("clubId", "==", clubId),
      where("date", ">=", startDate),
      where("date", "<=", endDate),
      orderBy("date", "desc"),
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(
      (doc) => ({ id: doc.id, ...doc.data() }) as Attendance,
    );
  },

  async create(
    data: Omit<
      Attendance,
      "id" | "createdAt" | "updatedAt" | "createdBy" | "updatedBy"
    >,
    userId: string,
  ): Promise<string> {
    const now = Timestamp.now();
    const docRef = await addDoc(collection(db, ATTENDANCE_COLLECTION), {
      ...data,
      createdAt: now,
      updatedAt: now,
      createdBy: userId,
      updatedBy: userId,
    });
    return docRef.id;
  },

  async update(
    id: string,
    data: Partial<Attendance>,
    userId?: string,
  ): Promise<void> {
    const docRef = doc(db, ATTENDANCE_COLLECTION, id);
    await updateDoc(docRef, {
      ...data,
      updatedAt: Timestamp.now(),
      ...(userId && { updatedBy: userId }),
    });
  },

  async updateStatus(
    id: string,
    status: AttendanceStatus,
    justification?: string,
  ): Promise<void> {
    const docRef = doc(db, ATTENDANCE_COLLECTION, id);
    await updateDoc(docRef, {
      status,
      justification: justification || null,
      updatedAt: Timestamp.now(),
    });
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, ATTENDANCE_COLLECTION, id));
  },

  // Crear asistencia masiva para los miembros de una reunión.
  // IDEMPOTENTE: usa docId determinístico `${meetingId}__${memberId}` y NO
  // sobrescribe registros ya existentes (no resetea estados ya marcados). Así
  // se puede re-ejecutar para "agregar miembros faltantes" sin duplicar ni
  // pisar la asistencia ya tomada.
  async createBulkForMeeting(
    meetingId: string,
    clubId: string,
    date: Timestamp,
    members: Array<{ memberId: string; personId: string; personName: string }>,
    defaultStatus: AttendanceStatus = "absent",
    userId: string,
  ): Promise<number> {
    // Traer existentes para no duplicar ni pisar.
    const existingSnap = await getDocs(
      query(
        collection(db, ATTENDANCE_COLLECTION),
        where("meetingId", "==", meetingId),
      ),
    );
    const existingMemberIds = new Set(
      existingSnap.docs.map((d) => (d.data() as Attendance).memberId),
    );
    const toCreate = members.filter((m) => !existingMemberIds.has(m.memberId));
    if (toCreate.length === 0) return 0;

    const now = Timestamp.now();
    const batch = writeBatch(db);
    toCreate.forEach((member) => {
      const docRef = doc(
        db,
        ATTENDANCE_COLLECTION,
        `${meetingId}__${member.memberId}`,
      );
      batch.set(docRef, {
        type: "meeting" as const,
        meetingId,
        clubId,
        date,
        memberId: member.memberId,
        personId: member.personId,
        personName: member.personName,
        status: defaultStatus,
        createdAt: now,
        updatedAt: now,
        createdBy: userId,
        updatedBy: userId,
      });
    });

    await batch.commit();
    return toCreate.length;
  },

  // Actualizar múltiples registros de asistencia
  async updateBulk(
    updates: Array<{
      id: string;
      status: AttendanceStatus;
      justification?: string;
    }>,
  ): Promise<void> {
    const batch = writeBatch(db);
    const now = Timestamp.now();

    updates.forEach(({ id, status, justification }) => {
      const docRef = doc(db, ATTENDANCE_COLLECTION, id);
      batch.update(docRef, {
        status,
        justification: justification || null,
        updatedAt: now,
      });
    });

    await batch.commit();
  },

  // Obtener estadísticas de asistencia por reunión
  async getMeetingStats(meetingId: string): Promise<{
    total: number;
    present: number;
    absent: number;
    late: number;
    excused: number;
    attendanceRate: number;
  }> {
    const attendances = await this.getByMeeting(meetingId);

    const stats = {
      total: attendances.length,
      present: attendances.filter((a) => a.status === "present").length,
      absent: attendances.filter((a) => a.status === "absent").length,
      late: attendances.filter((a) => a.status === "late").length,
      excused: attendances.filter((a) => a.status === "excused").length,
      attendanceRate: 0,
    };

    if (stats.total > 0) {
      stats.attendanceRate = Math.round(
        ((stats.present + stats.late) / stats.total) * 100,
      );
    }

    return stats;
  },

  // Obtener estadísticas de asistencia por miembro
  async getMemberStats(
    memberId: string,
    startDate?: Timestamp,
    endDate?: Timestamp,
  ): Promise<{
    totalMeetings: number;
    present: number;
    absent: number;
    late: number;
    excused: number;
    attendanceRate: number;
  }> {
    let attendances = await this.getByMember(memberId);

    if (startDate && endDate) {
      attendances = attendances.filter(
        (a) => a.date >= startDate && a.date <= endDate,
      );
    }

    const stats = {
      totalMeetings: attendances.length,
      present: attendances.filter((a) => a.status === "present").length,
      absent: attendances.filter((a) => a.status === "absent").length,
      late: attendances.filter((a) => a.status === "late").length,
      excused: attendances.filter((a) => a.status === "excused").length,
      attendanceRate: 0,
    };

    if (stats.totalMeetings > 0) {
      stats.attendanceRate = Math.round(
        ((stats.present + stats.late) / stats.totalMeetings) * 100,
      );
    }

    return stats;
  },
};

// ==================== HELPERS ====================

export function getMeetingTypeLabel(type: MeetingType): string {
  const labels: Record<MeetingType, string> = {
    regular: "Reunión Regular",
    special: "Reunión Especial",
    training: "Capacitación",
    service: "Servicio Comunitario",
    other: "Otro",
  };
  return labels[type];
}

export function getMeetingTypeColor(type: MeetingType): string {
  const colors: Record<MeetingType, string> = {
    regular: "bg-blue-100 text-blue-700",
    special: "bg-purple-100 text-purple-700",
    training: "bg-green-100 text-green-700",
    service: "bg-orange-100 text-orange-700",
    other: "bg-gray-100 text-gray-700",
  };
  return colors[type];
}

export function getAttendanceStatusLabel(status: AttendanceStatus): string {
  const labels: Record<AttendanceStatus, string> = {
    present: "Presente",
    absent: "Ausente",
    late: "Tardanza",
    excused: "Justificado",
  };
  return labels[status];
}

export function getAttendanceStatusColor(status: AttendanceStatus): string {
  const colors: Record<AttendanceStatus, string> = {
    present: "bg-green-100 text-green-700",
    absent: "bg-red-100 text-red-700",
    late: "bg-yellow-100 text-yellow-700",
    excused: "bg-blue-100 text-blue-700",
  };
  return colors[status];
}

// ==================== REALTIME SUBSCRIPTIONS ====================

export function subscribeToMeetingsByClubAndPeriod(
  clubId: string,
  periodId: string,
  callback: (meetings: Meeting[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, MEETINGS_COLLECTION),
    where("clubId", "==", clubId),
    where("periodId", "==", periodId),
    orderBy("date", "desc"),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) => convertTimestamps({ id: d.id, ...d.data() }) as Meeting,
        ),
      );
    },
    (error) => {
      console.error(
        "[Firestore] subscribeToMeetingsByClubAndPeriod error:",
        error,
      );
      if (onError) onError(error);
    },
  );
}

export function subscribeToMeetingsByClub(
  clubId: string,
  callback: (meetings: Meeting[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, MEETINGS_COLLECTION),
    where("clubId", "==", clubId),
    orderBy("date", "desc"),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) => convertTimestamps({ id: d.id, ...d.data() }) as Meeting,
        ),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToMeetingsByClub error:", error);
      if (onError) onError(error);
    },
  );
}

export function subscribeToAttendanceByMeeting(
  meetingId: string,
  callback: (attendance: Attendance[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, ATTENDANCE_COLLECTION),
    where("meetingId", "==", meetingId),
    orderBy("personName", "asc"),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) => convertTimestamps({ id: d.id, ...d.data() }) as Attendance,
        ),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToAttendanceByMeeting error:", error);
      if (onError) onError(error);
    },
  );
}

export function subscribeToAttendanceByEvent(
  eventId: string,
  callback: (attendance: Attendance[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, ATTENDANCE_COLLECTION),
    where("eventId", "==", eventId),
    orderBy("personName", "asc"),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) => convertTimestamps({ id: d.id, ...d.data() }) as Attendance,
        ),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToAttendanceByEvent error:", error);
      if (onError) onError(error);
    },
  );
}

export function subscribeToAttendanceByPerson(
  personId: string,
  callback: (attendance: Attendance[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, ATTENDANCE_COLLECTION),
    where("personId", "==", personId),
    orderBy("date", "desc"),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) => convertTimestamps({ id: d.id, ...d.data() }) as Attendance,
        ),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToAttendanceByPerson error:", error);
      if (onError) onError(error);
    },
  );
}

export function subscribeToAttendanceByMember(
  memberId: string,
  callback: (attendance: Attendance[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, ATTENDANCE_COLLECTION),
    where("memberId", "==", memberId),
    orderBy("date", "desc"),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) => convertTimestamps({ id: d.id, ...d.data() }) as Attendance,
        ),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToAttendanceByMember error:", error);
      if (onError) onError(error);
    },
  );
}

export function subscribeToMeetingsByType(
  clubId: string,
  type: MeetingType,
  callback: (meetings: Meeting[]) => void,
  onError?: (error: Error) => void,
): () => void {
  const q = query(
    collection(db, MEETINGS_COLLECTION),
    where("clubId", "==", clubId),
    where("type", "==", type),
    orderBy("date", "desc"),
  );
  return onSnapshot(
    q,
    (snapshot) => {
      callback(
        snapshot.docs.map(
          (d) => convertTimestamps({ id: d.id, ...d.data() }) as Meeting,
        ),
      );
    },
    (error) => {
      console.error("[Firestore] subscribeToMeetingsByType error:", error);
      if (onError) onError(error);
    },
  );
}

export function subscribeToMeeting(
  id: string,
  callback: (meeting: Meeting | null) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    doc(db, MEETINGS_COLLECTION, id),
    (docSnap) => {
      if (docSnap.exists()) {
        callback(
          convertTimestamps({ id: docSnap.id, ...docSnap.data() }) as Meeting,
        );
      } else {
        callback(null);
      }
    },
    (error) => {
      console.error("[Firestore] subscribeToMeeting error:", error);
      if (onError) onError(error);
    },
  );
}
